// Push Notifications Utility
// Handles push notification subscription and management for web chat

class PushNotificationManager {
  constructor() {
    this.vapidPublicKey = null;
    this.serviceWorkerUrl = null;
    this.subscriptionUrl = null;
    this.statusUrl = null;
    this.chatId = null;
    this.apiToken = null;
    this.isInitialized = false;
    this.showModalCallback = null; // Callback to show inline modal
  }

  // Set callback to show inline modal
  setShowModalCallback(callback) {
    this.showModalCallback = callback;
  }

  // Check if we should show the push notification prompt
  async shouldShowPrompt() {
    if (!this.isSupported()) return false;
    if (!this.isInitialized) return false;
    if (this.hasBeenAskedBefore()) return false;
    
    // Check if already enabled
    const alreadyEnabled = await this.isEnabledForChat();
    if (alreadyEnabled) return false;
    
    return true;
  }

  // Show the inline modal for push notification subscription
  async showInlineModal() {
    if (this.showModalCallback) {
      this.showModalCallback(true);
      return true;
    }
    return false;
  }

  // Get notification icon from localStorage
  getNotificationIcon() {
    return localStorage.getItem('logo') || '/favicon.ico';
  }

  // Get backend URL for service worker
  getBackendUrl() {
    // Check for global override first
    if (typeof window !== 'undefined' && window.AITOMOTIVE_BACKEND_URL) {
      return window.AITOMOTIVE_BACKEND_URL;
    }
    const backendDomain = process.env.REACT_APP_DOMAIN || 'http://127.0.0.1:8000/';
    return backendDomain.endsWith('/') ? backendDomain.slice(0, -1) : backendDomain;
  }

  // Initialize with configuration from serve_chat API
  init(config) {
    this.vapidPublicKey = config.vapid_public_key;
    this.serviceWorkerUrl = config.service_worker_url;
    this.subscriptionUrl = config.push_subscription_url;
    this.statusUrl = config.push_status_url;
    this.isInitialized = true;
  }

  // Set chat context
  setChatContext(chatId, apiToken) {
    this.chatId = chatId;
    this.apiToken = apiToken;
  }

  // Check if browser supports push notifications
  isSupported() {
    return 'serviceWorker' in navigator && 'PushManager' in window;
  }

  // Check if user has already been asked for permissions
  hasBeenAskedBefore() {
    return localStorage.getItem('push_notification_asked') === 'true';
  }

  // Mark that user has been asked
  markAsAsked() {
    localStorage.setItem('push_notification_asked', 'true');
  }

  // Get current permission status
  getPermissionStatus() {
    if (!this.isSupported()) return 'unsupported';
    return Notification.permission;
  }

  // Check if notifications are already enabled for this chat
  async isEnabledForChat() {
    if (!this.isInitialized || !this.chatId || !this.apiToken) {
      return false;
    }

    try {
      // Use getBackendUrl to ensure consistent URL handling
      const baseUrl = this.getBackendUrl();
      const url = `${baseUrl}${this.statusUrl}?chat_id=${this.chatId}&api_token=${this.apiToken}`;
      
      // Checking push notification status
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.push_notifications_enabled;
      } else {
        console.error('Failed to check push notification status - server response:', {
          status: response.status,
          statusText: response.statusText
        });
      }
    } catch (error) {
      console.error('Failed to check push notification status:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
    }
    return false;
  }

  // Check if we're on a third-party domain (different from chat widget origin)
  isThirdPartyDomain() {
    const chatWidgetOrigin = 'https://chat-aitomotive.pages.dev';
    // In development, consider localhost as same-origin
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
      return false;
    }
    return window.location.origin !== chatWidgetOrigin;
  }

  // Force popup mode for testing (set to true to test popup flow locally)
  forcePopupMode() {
    return false; // Set to true to test external popup flow
  }

  // Open popup for push notification subscription on our domain
  async openSubscriptionPopup() {
    // Use localhost in development, production URL otherwise
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const chatWidgetOrigin = isLocalDev 
      ? window.location.origin  // Use same origin for local testing
      : 'https://chat-aitomotive.pages.dev';
    
    const popupUrl = `${chatWidgetOrigin}/push-subscribe.html?` + new URLSearchParams({
      chatId: this.chatId || '',
      apiToken: this.apiToken || '',
      vapidKey: this.vapidPublicKey || '',
      backendUrl: this.getBackendUrl(),
      subscriptionUrl: this.subscriptionUrl || '',
      returnOrigin: window.location.origin
    }).toString();

    return new Promise((resolve) => {
      // Open popup
      const popup = window.open(
        popupUrl,
        'aitomotive_push_subscribe',
        'width=450,height=600,scrollbars=yes,resizable=yes'
      );

      if (!popup) {
        console.warn('Popup blocked - please allow popups for push notifications');
        resolve({ success: false, error: 'Popup bloccato. Abilita i popup per questo sito.' });
        return;
      }

      // Listen for message from popup
      const messageHandler = (event) => {
        if (event.origin !== chatWidgetOrigin) return;
        
        if (event.data.type === 'PUSH_SUBSCRIPTION_RESULT') {
          window.removeEventListener('message', messageHandler);
          
          if (event.data.success) {
            console.log('Push notifications enabled via popup!');
            this.markAsAsked();
            resolve({ success: true });
          } else if (event.data.denied) {
            console.warn('Push notification permission denied in popup');
            resolve({ success: false, denied: true });
          } else {
            console.warn('Push notification subscription failed in popup:', event.data.error);
            resolve({ success: false, error: event.data.error || 'Errore durante la configurazione' });
          }
        }
      };

      window.addEventListener('message', messageHandler);

      // Cleanup if popup is closed without completing
      const checkPopup = setInterval(() => {
        if (popup.closed) {
          clearInterval(checkPopup);
          window.removeEventListener('message', messageHandler);
          resolve({ success: false, error: 'Popup chiuso prima del completamento' });
        }
      }, 500);

      // Timeout after 2 minutes
      setTimeout(() => {
        clearInterval(checkPopup);
        window.removeEventListener('message', messageHandler);
        if (!popup.closed) popup.close();
        resolve({ success: false, error: 'Timeout - operazione troppo lunga' });
      }, 120000);
    });
  }

  // Request permission and setup push notifications
  async requestPermission() {
    if (!this.isSupported()) {
      console.warn('Push notifications not supported in this browser');
      return false;
    }
    
    if (!this.isInitialized) {
      console.warn('Push notification manager not initialized');
      return false;
    }

    try {
      // Debug information
      console.log('Setting up push notifications with config:', {
        vapidPublicKey: this.vapidPublicKey ? 'present' : 'missing',
        serviceWorkerUrl: this.serviceWorkerUrl,
        subscriptionUrl: this.subscriptionUrl,
        statusUrl: this.statusUrl,
        chatId: this.chatId,
        apiToken: this.apiToken ? 'present' : 'missing',
        isThirdParty: this.isThirdPartyDomain()
      });

      // Mark as asked before requesting permission
      this.markAsAsked();

      // Request permission
      console.log('Requesting notification permission...');
      const permission = await Notification.requestPermission();
      console.log('Permission result:', permission);
      
      if (permission !== 'granted') {
        console.warn('Notification permission not granted:', permission);
        return false;
      }

      // Check if we should force popup mode for testing
      if (this.forcePopupMode()) {
        console.log('🧪 Force popup mode enabled - using popup-based subscription...');
        return await this.openSubscriptionPopup();
      }

      // Try to register service worker - first check for existing registration
      let registration;
      const serviceWorkerUrl = this.serviceWorkerUrl || '/service-worker.js';
      
      try {
        // First check if service worker is already registered
        const existingRegistration = await navigator.serviceWorker.getRegistration();
        if (existingRegistration) {
          console.log('✅ Service worker already registered - using existing registration');
          registration = existingRegistration;
        } else {
          // Check for GTM-injected service worker
          if (typeof window !== 'undefined' && window.AITOMOTIVE_SW_REGISTRATION) {
            console.log('✅ Using pre-registered service worker from GTM');
            registration = window.AITOMOTIVE_SW_REGISTRATION;
          } else {
            console.log('Attempting to register service worker:', serviceWorkerUrl, 'on domain:', window.location.origin);
            
            // Try to register service worker directly
            registration = await navigator.serviceWorker.register(serviceWorkerUrl, { scope: '/' });
            console.log('✅ Service worker registered successfully - push notifications enabled');
          }
        }
      } catch (error) {
        console.log('Direct service worker registration failed:', error.message);
        
        // Check if we're on a third-party domain
        if (this.isThirdPartyDomain()) {
          console.log('On third-party domain - attempting popup-based subscription...');
          return await this.openSubscriptionPopup();
        }
        
        console.log('Attempting dynamic injection...');
        
        // Try to create and inject service worker dynamically
        try {
          registration = await this.createAndInjectServiceWorker();
          console.log('✅ Service worker injected and registered successfully - push notifications enabled');
        } catch (injectionError) {
          console.error('Dynamic injection failed:', injectionError);
          
          // Last resort: try popup approach
          console.log('Attempting popup-based subscription as fallback...');
          return await this.openSubscriptionPopup();
        }
      }
      
      // Wait for service worker to be ready
      await navigator.serviceWorker.ready;
      console.log('Service worker ready');

      // Subscribe to push notifications
      console.log('Subscribing to push notifications...');
      let subscription;
      try {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(this.vapidPublicKey)
        });
        console.log('Push subscription created successfully');
      } catch (subscriptionError) {
        console.error('Failed to create push subscription:', {
          message: subscriptionError.message,
          name: subscriptionError.name,
          error: subscriptionError
        });
        return false;
      }

      // Save subscription to backend
      console.log('Saving subscription to backend...');
      try {
        const result = await this.saveSubscription(subscription);
        console.log('Subscription save result:', result);
        if (result) {
          console.log('🎉 Push notifications fully enabled!');
        } else {
          console.warn('⚠️ Push subscription created but failed to save to backend');
        }
        return result;
      } catch (saveError) {
        console.error('Failed to save subscription to backend:', {
          message: saveError.message,
          name: saveError.name,
          error: saveError
        });
        return false;
      }

    } catch (error) {
      console.error('Error setting up push notifications:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        error: error
      });
      
      // Additional debugging for specific error types
      if (error.name === 'NotSupportedError') {
        console.error('Push notifications not supported by this browser or in this context');
      } else if (error.name === 'NotAllowedError') {
        console.error('Push notifications permission denied by user');
      } else if (error.name === 'InvalidStateError') {
        console.error('Service worker registration failed or is in invalid state');
      } else if (error.name === 'SecurityError') {
        console.error('Security error - check HTTPS and service worker origin');
      }
      
      return false;
    }
  }

  // Save subscription to backend
  async saveSubscription(subscription) {
    if (!this.chatId || !this.apiToken) {
      console.error('Cannot save subscription: missing chatId or apiToken', {
        chatId: this.chatId ? 'present' : 'missing',
        apiToken: this.apiToken ? 'present' : 'missing'
      });
      return false;
    }

    try {
      const payload = {
        chat_id: this.chatId,
        api_token: this.apiToken,
        subscription: subscription.toJSON()
      };
      
      // Use getBackendUrl to ensure consistent URL handling
      const baseUrl = this.getBackendUrl();
      const subscriptionUrl = `${baseUrl}${this.subscriptionUrl}`;
      
      console.log('Sending subscription to:', subscriptionUrl);
      
      const response = await fetch(subscriptionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Failed to save subscription - server response:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        return false;
      }
      
      console.log('Subscription saved successfully');
      return true;
    } catch (error) {
      console.error('Failed to save subscription:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        error: error
      });
      return false;
    }
  }

  // Create and inject service worker dynamically
  async createAndInjectServiceWorker() {
    console.log('Creating dynamic service worker...');
    
    try {
      // First check if there's a GTM-injected service worker
      if (typeof window !== 'undefined' && window.AITOMOTIVE_SW_REGISTRATION) {
        console.log('✅ Using pre-registered service worker from GTM');
        return window.AITOMOTIVE_SW_REGISTRATION;
      }
      
      // Try to get service worker content from GTM
      if (typeof window !== 'undefined' && window.AITOMOTIVE_SW_CONTENT) {
        console.log('Using service worker content from GTM');
        const blob = new Blob([window.AITOMOTIVE_SW_CONTENT], { type: 'application/javascript' });
        const blobUrl = URL.createObjectURL(blob);
        
        try {
          const registration = await navigator.serviceWorker.register(blobUrl, { scope: '/' });
          console.log('✅ Service worker registered from GTM content');
          
          // Clean up the blob URL after a delay
          setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
          
          return registration;
        } catch (error) {
          console.error('Failed to register service worker from GTM content:', error);
        }
      }
      
      // Try to fetch from various sources
      let serviceWorkerContent = null;
      
      // Try fetching from local first
      try {
        const localResponse = await fetch('/service-worker.js');
        if (localResponse.ok) {
          serviceWorkerContent = await localResponse.text();
          console.log('Service worker content fetched from local path');
        }
      } catch (localError) {
        console.log('Could not fetch local service worker:', localError.message);
      }
      
      // If local fetch failed, try from chat app deployment
      if (!serviceWorkerContent) {
        try {
          const baseUrl = 'https://chat-aitomotive.pages.dev';
          const response = await fetch(`${baseUrl}/service-worker.js`);
          
          if (response.ok) {
            serviceWorkerContent = await response.text();
            console.log('Service worker content fetched from chat app deployment');
          } else {
            throw new Error(`Failed to fetch service worker: ${response.status}`);
          }
        } catch (remoteError) {
          console.error('Could not fetch remote service worker:', remoteError.message);
        }
      }
      
      // If we have content, try to register it
      if (serviceWorkerContent) {
        // Create a blob URL
        const blob = new Blob([serviceWorkerContent], { type: 'application/javascript' });
        const blobUrl = URL.createObjectURL(blob);
        
        try {
          // Try blob URL approach
          const registration = await navigator.serviceWorker.register(blobUrl, {
            scope: '/'
          });
          console.log('✅ Service worker registered via blob URL');
          
          // Clean up the blob URL after a delay
          setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);
          
          return registration;
        } catch (blobError) {
          console.error('Blob URL approach failed:', {
            message: blobError.message,
            name: blobError.name,
            stack: blobError.stack
          });
          
          // Fallback to fetch interception
          return await this.createServiceWorkerWithFetchInterception(serviceWorkerContent);
        }
      }
      
      // If we couldn't get service worker content from anywhere
      throw new Error('Could not obtain service worker content from any source');
    } catch (error) {
      console.error('Failed to create dynamic service worker:', {
        message: error.message,
        name: error.name,
        stack: error.stack
      });
      throw error;
    }
  }

  // Create service worker using fetch interception
  async createServiceWorkerWithFetchInterception(serviceWorkerContent) {
    // Store the service worker content globally
    window.__aitomotiveServiceWorkerContent = serviceWorkerContent;
    
    // Override fetch for service worker requests
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const [resource, init] = args;
      
      // Intercept service worker requests
      if (typeof resource === 'string' && (
        resource.endsWith('/service-worker.js') || 
        resource.endsWith('/aitomotive-chat-sw.js')
      )) {
        console.log('Intercepting service worker request:', resource);
        
        // Return the service worker content
        return Promise.resolve(new Response(window.__aitomotiveServiceWorkerContent, {
          status: 200,
          statusText: 'OK',
          headers: {
            'Content-Type': 'application/javascript',
            'Cache-Control': 'no-cache'
          }
        }));
      }
      
      // For all other requests, use original fetch
      return originalFetch.apply(this, args);
    };
    
    try {
      // Now try to register the service worker
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      console.log('Service worker registered via fetch interception');
      return registration;
    } finally {
      // Restore original fetch after a delay to avoid interfering with other requests
      setTimeout(() => {
        window.fetch = originalFetch;
        delete window.__aitomotiveServiceWorkerContent;
        console.log('Fetch interception cleaned up');
      }, 10000);
    }
  }

  // Convert VAPID key to Uint8Array
  urlBase64ToUint8Array(base64String) {
    try {
      if (!base64String) {
        throw new Error('VAPID key is empty or undefined');
      }
      
      const padding = '='.repeat((4 - base64String.length % 4) % 4);
      const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

      const rawData = window.atob(base64);
      const outputArray = new Uint8Array(rawData.length);

      for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
      }
      return outputArray;
    } catch (error) {
      console.error('Failed to convert VAPID key to Uint8Array:', {
        vapidKey: base64String,
        error: error.message
      });
      throw error;
    }
  }

  // Main method to handle push notification setup
  async setupPushNotifications() {
    try {
      console.log('Starting push notification setup...');
      
      // Check if service worker URL is configured
      if (!this.serviceWorkerUrl) {
        console.info('No service worker URL configured, skipping push notifications');
        return false;
      }
      
      // Don't ask if already asked before
      if (this.hasBeenAskedBefore()) {
        console.log('Push notifications already asked before, skipping');
        return false;
      }

      // Don't ask if notifications already enabled for this chat
      console.log('Checking if notifications already enabled for this chat...');
      const alreadyEnabled = await this.isEnabledForChat();
      if (alreadyEnabled) {
        console.log('Push notifications already enabled for this chat');
        return false;
      }

      // If we have a modal callback, show the inline modal instead of directly requesting
      if (this.showModalCallback) {
        console.log('Showing inline push notification modal...');
        return await this.showInlineModal();
      }

      // Fallback: Request permission directly
      console.log('Proceeding with direct permission request...');
      return await this.requestPermission();
    } catch (error) {
      console.error('Error in push notifications setup:', {
        message: error.message,
        name: error.name,
        stack: error.stack,
        error: error
      });
      return false;
    }
  }


}

// Create singleton instance
const pushNotificationManager = new PushNotificationManager();

// Expose globally for debugging
if (typeof window !== 'undefined') {
  window.pushNotificationManager = pushNotificationManager;
}

export default pushNotificationManager;
